import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Building2, Phone, Mail, MapPin, Globe, FileText, Save, ImagePlus, X, Check } from 'lucide-react';

export default function ClinicaPerfilPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [form, setForm] = useState({ nome: '', telefone: '', email: '', endereco: '', site: '', descricao: '' });
  const [logos, setLogos] = useState({ logo1Url: null, logo2Url: null, logoAtiva: null });
  const fileRef1 = useRef();
  const fileRef2 = useRef();

  useEffect(() => {
    api.get('/clinica/perfil').then(({ data }) => {
      const d = data.data;
      setForm({
        nome: d.nome || '',
        telefone: d.telefone || '',
        email: d.email || '',
        endereco: d.endereco || '',
        site: d.site || '',
        descricao: d.descricao || '',
      });
      setLogos({ logo1Url: d.logo1Url || null, logo2Url: d.logo2Url || null, logoAtiva: d.logoAtiva });
    }).catch(() => toast.error('Erro ao carregar perfil'))
      .finally(() => setLoading(false));
  }, []);

  const handleSalvar = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/clinica/perfil', form);
      toast.success('Perfil atualizado!');
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const handleUploadLogo = async (slot, file) => {
    if (!file) return;
    setUploadingSlot(slot);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const { data } = await api.post(`/clinica/perfil/logo/${slot}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLogos(prev => ({ ...prev, [`logo${slot}Url`]: data.data[`logo${slot}Url`], logoAtiva: data.data.logoAtiva }));
      toast.success('Logo enviada!');
    } catch { toast.error('Erro ao enviar logo'); }
    finally { setUploadingSlot(null); }
  };

  const handleDeleteLogo = async (slot) => {
    setUploadingSlot(slot);
    try {
      const { data } = await api.delete(`/clinica/perfil/logo/${slot}`);
      setLogos({ logo1Url: data.data.logo1Url, logo2Url: data.data.logo2Url, logoAtiva: data.data.logoAtiva });
      toast.success('Logo removida.');
    } catch { toast.error('Erro ao remover logo'); }
    finally { setUploadingSlot(null); }
  };

  const handleSetLogoAtiva = async (slot) => {
    try {
      await api.patch('/clinica/perfil/logo-ativa', { logoAtiva: slot });
      setLogos(prev => ({ ...prev, logoAtiva: slot }));
      toast.success('Logo ativa atualizada!');
    } catch { toast.error('Erro ao atualizar logo ativa'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  const LogoSlot = ({ slot }) => {
    const url = logos[`logo${slot}Url`];
    const ativa = logos.logoAtiva === slot;
    const ref = slot === 1 ? fileRef1 : fileRef2;

    return (
      <div className={`relative border-2 rounded-2xl p-4 flex flex-col items-center gap-3 transition-all ${ativa ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
        {ativa && (
          <span className="absolute top-2 right-2 flex items-center gap-1 text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
            <Check className="w-3 h-3" /> Ativa nos PDFs
          </span>
        )}
        <p className="text-sm font-medium text-gray-600 self-start">Logo {slot}</p>

        {url ? (
          <div className="w-full flex flex-col items-center gap-3">
            <img src={url} alt={`Logo ${slot}`} className="h-20 object-contain rounded-lg border border-gray-200 bg-white p-2 w-full" />
            <div className="flex gap-2 w-full">
              {!ativa && (
                <button onClick={() => handleSetLogoAtiva(slot)} disabled={!!uploadingSlot}
                  className="flex-1 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-40">
                  Usar nos PDFs
                </button>
              )}
              <button onClick={() => handleDeleteLogo(slot)} disabled={!!uploadingSlot}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors disabled:opacity-40">
                <X className="w-3.5 h-3.5" /> Remover
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => ref.current?.click()} disabled={!!uploadingSlot}
            className="flex flex-col items-center gap-2 w-full py-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all text-gray-400 hover:text-blue-500 disabled:opacity-40">
            {uploadingSlot === slot ? (
              <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <ImagePlus className="w-6 h-6" />
                <span className="text-xs font-medium">Clique para enviar</span>
                <span className="text-xs text-gray-400">JPG, PNG ou WebP · max 2MB</span>
              </>
            )}
          </button>
        )}
        <input ref={ref} type="file" accept="image/*" className="hidden"
          onChange={e => { if (e.target.files[0]) handleUploadLogo(slot, e.target.files[0]); e.target.value = ''; }} />
      </div>
    );
  };

  const fields = [
    { key: 'nome', label: 'Nome da Clínica', icon: Building2, type: 'text', required: true },
    { key: 'telefone', label: 'Telefone', icon: Phone, type: 'text' },
    { key: 'email', label: 'Email', icon: Mail, type: 'email' },
    { key: 'endereco', label: 'Endereço', icon: MapPin, type: 'text' },
    { key: 'site', label: 'Site', icon: Globe, type: 'text', placeholder: 'https://...' },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Perfil da Clínica</h1>
        <p className="text-gray-500 mt-1">Gerencie os dados e a identidade visual da sua clínica</p>
      </div>

      <div className="space-y-6">

        {/* Logos */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Logos</h2>
          <p className="text-sm text-gray-500 mb-5">As logos aparecem no cabeçalho de todos os documentos PDF gerados pela clínica. A logo marcada como "Ativa" é a que aparece nos PDFs.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <LogoSlot slot={1} />
            <LogoSlot slot={2} />
          </div>
        </div>

        {/* Dados da clínica */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-5">Dados da Clínica</h2>
          <form onSubmit={handleSalvar} className="space-y-4">
            {fields.map(({ key, label, icon: Icon, type, placeholder, required }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}{required && ' *'}</label>
                <div className="relative">
                  <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type={type}
                    value={form[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder || ''}
                    required={required}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none"
                  />
                </div>
              </div>
            ))}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="flex items-center gap-1.5"><FileText className="w-4 h-4 text-gray-400" /> Descrição / Sobre a clínica</span>
              </label>
              <textarea
                rows={3}
                value={form.descricao}
                onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
                placeholder="Uma breve descrição da sua clínica..."
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none resize-none"
              />
            </div>

            <button type="submit" disabled={saving}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-xl text-sm font-medium transition-colors">
              <Save className="w-4 h-4" />
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
}